import { useEffect } from "react";
import { Outlet, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { Toaster } from "@/components/ui/sonner";
import { Splash } from "@/components/shell/splash";
import { RedirectToLogin } from "@/components/shell/redirect-to-login";
import { BackLink } from "@/components/shell/back-link";
import { Wordmark } from "@/components/shell/wordmark";
import { AccountCard, LeaveAndLegal, SettingsNav } from "@/components/settings/frame";

// Settings: a page per subject (the home, the workspaces, your profile, the
// language, saving from shops, help, deleting the account), in a frame whose
// rail starts with your card. Each page is a child route under /settings.
export const Route = createFileRoute("/settings")({
  component: SettingsLayout,
});

function SettingsLayout() {
  const { t: tc } = useTranslation();
  return (
    <>
      <AuthLoading>
        <Splash subtitle={tc("status.loading")} />
      </AuthLoading>
      <Unauthenticated>
        <RedirectToLogin />
      </Unauthenticated>
      <Authenticated>
        <SignedInSettings />
      </Authenticated>
      <Toaster />
    </>
  );
}

function SignedInSettings() {
  const { t } = useTranslation("settings");
  const { t: tc } = useTranslation();
  const workspace = useQuery(api.workspaces.getMyWorkspace);
  const plan = useQuery(api.billing.getMyPlan);
  const companies = useQuery(api.organizations.mine);

  if (workspace === undefined) return <Splash subtitle={tc("status.loading")} />;
  if (workspace === null) return <RedirectToOnboarding />;

  return (
    <main className="mx-auto min-h-svh w-full max-w-3xl px-6 pb-24 md:max-w-6xl md:px-10">
      <div className="sticky top-0 z-30 -mx-6 border-b border-line bg-paper/85 px-6 backdrop-blur-md md:-mx-10 md:px-10">
        <div className="flex h-12 items-center justify-between">
          <BackLink to="/" label={t("page.backToBoards")} />
          <Wordmark size="sm" />
        </div>
      </div>

      <div className="grid gap-8 pt-8 md:grid-cols-[14.5rem_minmax(0,1fr)] md:gap-16 md:pt-12 lg:gap-20">
        <aside className="grid min-w-0 grid-cols-1 content-start gap-4 md:sticky md:top-24 md:gap-5 md:self-start">
          <AccountCard />
          <SettingsNav
            homeName={workspace.name}
            studio={plan?.plan === "studio"}
            clients={workspace.role === "team" && workspace.can.clients}
            company={companies?.some((c) => c.kind === "studio") ?? false}
          />
        </aside>
        <div className="min-w-0 max-w-[42.5rem]">
          <Outlet />
          {/* On a phone the rail is a strip: Delete and the legal pages come at the end instead. */}
          <div className="mt-20 md:hidden">
            <LeaveAndLegal />
          </div>
        </div>
      </div>
    </main>
  );
}

function RedirectToOnboarding() {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  useEffect(() => {
    navigate({ to: "/onboarding" });
  }, [navigate]);
  return <Splash subtitle={t("page.redirecting")} />;
}
